import React from 'react';
import { Text, RichText, Link, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import './styles.css';
import RenderingStyling from '../../../lib/RenderingStyling';
import { addStyle, getBackgroundImages, getTrackingAttributes } from '../SharedUtils/utils';
import { useSubtitleClasses } from '../FullWidthRichText/useSubtitleClasses';
import { renderSubTitle } from '../FullWidthRichText/fieldsRenderer';

import { getBackgroundColor } from '../SharedUtils/themeColors';
import { useInvertedButton } from '../SharedUtils/hooks/useInvertedButton';
import { useInvertedColor } from '../SharedUtils/hooks/useInvertedColor';

import renderCard from './card';
import useCarousel from './carouselHook';
import useAdsStyles from './adsCardStylesOverrideHook';
import useTheme from '../SharedUtils/hooks';

const CarouselCard = ({ fields: fieldsNullable, rendering, sitecoreContext }) => {
  const fields = typeof fieldsNullable !== 'undefined' ? fieldsNullable : {};

  const componentId = `c-${rendering?.uid}`;
  const isEditorMode = sitecoreContext?.pageEditing ?? false;

  const { isLight } = useTheme();

  const widgetClasses = ['o-widget', 'c-carousel'];
  const widgetStyles = {};

  const subTitleClasses = useSubtitleClasses('c-carousel__subtitle', fields);

  addStyle(widgetStyles, '--carousel-padding-top', fields.PaddingTop?.value);
  addStyle(widgetStyles, '--carousel-padding-bottom', fields.PaddingBottom?.value);

  if (fields.BackgroundImageCover?.value) widgetClasses.push('u-bg-size-cover');

  const { largeBackgroundImageSrc } = getBackgroundImages(fields, isLight);
  addStyle(widgetStyles, 'backgroundImage', `url('${largeBackgroundImageSrc}')`);

  const isCtaInverted = useInvertedButton(fields);
  const isColorInverted = useInvertedColor(fields);

  if (isColorInverted) widgetClasses.push('is-inverted');

  const backgroundColor = getBackgroundColor(fields, isLight);
  addStyle(widgetStyles, 'backgroundColor', backgroundColor);

  addStyle(
    widgetStyles,
    '--carousel-nav-arrow-color',
    isLight ? fields.arrowColorLight?.value : fields.arrowColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-nav-arrow-shadow-color',
    isLight ? fields.arrowShadowColorLight?.value : fields.arrowShadowColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-nav-arrow-hover-color',
    isLight ? fields.arrowHoverColorLight?.value : fields.arrowHoverColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-nav-arrow-hover-shadow-color',
    isLight ? fields.arrowHoverShadowColorLight?.value : fields.arrowHoverShadowColorDark?.value
  );

  addStyle(
    widgetStyles,
    '--carousel-indicator-active-fill-color',
    isLight ? fields.activeFillColorLight?.value : fields.activeFillColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-indicator-inactive-fill-color',
    isLight ? fields.inactiveFillColorLight?.value : fields.inactiveFillColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-indicator-inactive-border-color',
    isLight ? fields.inactiveBorderColorLight?.value : fields.inactiveBorderColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-indicator-inactive-hover-border-color',
    isLight
      ? fields.inactiveHoverBorderColorLight?.value
      : fields.inactiveHoverBorderColorDark?.value
  );
  addStyle(
    widgetStyles,
    '--carousel-indicator-hover-fill-color',
    isLight ? fields.hoverFillColorLight?.value : fields.hoverFillColorDark?.value
  );

  const trackingAttributes = getTrackingAttributes(fields);

  useCarousel(componentId);
  useAdsStyles();

  const renderHeadline = () => {
    if (!isEditorMode && !fields.HeadlineRTE?.value) {
      return <></>;
    }

    const rawHeadline = getRawHeadline();
    if (!isEditorMode) {
      switch (fields.HeadingType?.value) {
        case 'H1':
          return (
            <div className="c-carousel__headline">
              <h1
                className="u-text-lg-1 u-text-md-1 u-text-sm-1"
                tabIndex="0"
                dangerouslySetInnerHTML={{ __html: rawHeadline }}
              >
                {renderHeadlingField()}
              </h1>
            </div>
          );

        case 'H2':
          return (
            <div className="c-carousel__headline">
              <h2
                className="u-text-lg-2 u-text-md-2 u-text-sm-2"
                tabIndex="0"
                dangerouslySetInnerHTML={{ __html: rawHeadline }}
              >
                {renderHeadlingField()}
              </h2>
            </div>
          );

        case 'H3':
          return (
            <div className="c-carousel__headline">
              <h3
                className="u-text-lg-3 u-text-md-3 u-text-sm-3"
                tabIndex="0"
                dangerouslySetInnerHTML={{ __html: rawHeadline }}
              >
                {renderHeadlingField()}
              </h3>
            </div>
          );

        case 'Div H1':
          return (
            <div
              className="c-carousel__headline u-text-lg-1 u-text-md-1 u-text-sm-1"
              dangerouslySetInnerHTML={{ __html: rawHeadline }}
            >
              {renderHeadlingField()}
            </div>
          );

        case 'Div H2':
          return (
            <div
              className="c-carousel__headline u-text-lg-2 u-text-md-2 u-text-sm-2"
              dangerouslySetInnerHTML={{ __html: rawHeadline }}
            >
              {renderHeadlingField()}
            </div>
          );

        case 'Div H3':
          return (
            <div
              className="c-carousel__headline u-text-lg-3 u-text-md-3 u-text-sm-3"
              dangerouslySetInnerHTML={{ __html: rawHeadline }}
            >
              {renderHeadlingField()}
            </div>
          );

        default:
          return (
            <div
              className="c-carousel__headline u-text-lg-2 u-text-md-2 u-text-sm-2"
              dangerouslySetInnerHTML={{ __html: rawHeadline }}
            >
              {renderHeadlingField()}
            </div>
          );
      }
    }
    if (isEditorMode) {
      switch (fields.HeadingType?.value) {
        case 'H1':
          return (
            <div className="c-carousel__headline">
              <h1 className="u-text-lg-1 u-text-md-1 u-text-sm-1" tabIndex="0">
                {renderHeadlingField()}
              </h1>
            </div>
          );

        case 'H2':
          return (
            <div className="c-carousel__headline">
              <h2 className="u-text-lg-2 u-text-md-2 u-text-sm-2" tabIndex="0">
                {renderHeadlingField()}
              </h2>
            </div>
          );

        case 'H3':
          return (
            <div className="c-carousel__headline">
              <h3 className="u-text-lg-3 u-text-md-3 u-text-sm-3" tabIndex="0">
                {renderHeadlingField()}
              </h3>
            </div>
          );

        case 'Div H1':
          return (
            <div className="c-carousel__headline u-text-lg-1 u-text-md-1 u-text-sm-1">
              {renderHeadlingField()}
            </div>
          );

        case 'Div H2':
          return (
            <div className="c-carousel__headline u-text-lg-2 u-text-md-2 u-text-sm-2">
              {renderHeadlingField()}
            </div>
          );

        case 'Div H3':
          return (
            <div className="c-carousel__headline u-text-lg-3 u-text-md-3 u-text-sm-3">
              {renderHeadlingField()}
            </div>
          );

        default:
          return (
            <div className="c-carousel__headline u-text-lg-2 u-text-md-2 u-text-sm-2">
              {renderHeadlingField()}
            </div>
          );
      }
    }
  };

  const getRawHeadline = () => {
    if (!isEditorMode) {
      return fields.HeadlineRTE?.value?.replace('<p>', '').replace('</p>', '');
    }

    return null;
  };

  const renderHeadlingField = () => {
    if (isEditorMode) {
      return <Text field={fields.HeadlineRTE} />;
    }

    return null;
  };

  return (
    <div
      {...RenderingStyling(rendering.componentName)}
      className={widgetClasses.join(' ')}
      style={widgetStyles}
      id={componentId}
    >
      <div className="c-container">
        {renderSubTitle({ field: fields.SubTitle, classes: subTitleClasses })}

        {renderHeadline()}

        {(isEditorMode || fields.Description?.value) && (
          <div
            className="c-carousel__description u-text-body-lg u-text-body-md u-text-body-sm"
            tabIndex="0"
          >
            <RichText field={fields.Description} />
          </div>
        )}

        <div className="c-carousel-wrapper">
          <ul className="c-carousel-controls" aria-label="Carousel Navigation" tabIndex="0">
            <li className="prev" data-controls="prev" aria-controls="customize" tabIndex="0">
              <i className="far fa-angle-left fa-4x"></i>
            </li>
            <li className="next" data-controls="next" aria-controls="customize" tabIndex="0">
              <i className="far fa-angle-right fa-4x"></i>
            </li>
          </ul>

          <div className="c-carousel-slider">
            {fields.CarouselCardList?.map((card, i) =>
              renderCard(card.fields, isLight, isEditorMode, i)
            )}
          </div>
        </div>

        {(isEditorMode || fields.CallToAction?.value?.href) && (
          <div className="c-carousel__CTA">
            <Link
              field={fields.CallToAction}
              className={`c-button ${isCtaInverted ? 'c-button--cta-invert' : 'c-button--cta'}`}
              role="button"
              {...trackingAttributes}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default withSitecoreContext()(CarouselCard);
